import {useState, useEffect, useMemo} from 'react';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Alert, Collapse, Skeleton} from '@mui/material';
import {
  Alarm,
  Apps,
  Devices,
  PersonRounded,
  AccessTimeFilled,
  AccountCircle,
  Assignment,
  InsertChart,
  MusicNote,
  People,
  KeyboardArrowDown,
  BarChart,
  Favorite,
  Badge,
  DriveFolderUploadRounded,
  VideoSettings,
  Widgets,
  ViewComfyRounded,
} from '@mui/icons-material';
import SettingsAccessibilityIcon from '@mui/icons-material/SettingsAccessibility';
import {ListItemLink} from '../list-item-link/list-item-link';
import {ActivitiesSector} from '../../backend/interfaces/activities-sector';
import {useOffice} from '../../contexts/office/context';
import {ReactSVG} from 'react-svg';
import {Environment, ScreenPaths} from '../../enums';
import {useAppContext} from '../../contexts/app/context';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../utils/constants';
import {EntitySpecialPurpose} from '../../backend/enums/entity-special-purpose';

// !!! NOTE: This assumes sectors are the same for all entities, as specified in the utils/constants/hardcoded.ts file.
const SECTOR_ID_TO_PERMISSION_TYPE_MAPPING: Record<string, RolePermissionType> = {
  [BADL_SECTOR_ID]: RolePermissionType.Seniors_BADL,
  [IADL_SECTOR_ID]: RolePermissionType.Seniors_IADL,
  [CLINICAL_SUPPORT_SECTOR_ID]: RolePermissionType.Seniors_ClinicalSupport,
  [PERSONAL_DEVELOPMENT_SECTOR_ID]: RolePermissionType.Seniors_PersonalDevelopment,
};

export interface SeniorSidePanelComponentsProps {
  showExpanded?: boolean;
  activitiesSectors?: ActivitiesSector[] | null;
}

export const SeniorSidePanelComponents = ({
  showExpanded: sidePanelExpanded,
  activitiesSectors,
}: SeniorSidePanelComponentsProps) => {
  const [expandedPersonalData, setExpandedPersonalData] = useState(false);
  const [expandedStatistics, setExpandedStatistics] = useState(false);
  const [expandedCarePlan, setExpandedCarePlan] = useState(false);
  const [expandedDigitalInclusion, setExpandedDigitalInclusion] = useState(false);

  const officeContext = useOffice();
  const appContext = useAppContext();
  const canAccessDevelopmentFeatures =
    appContext.state.configuration.environment === Environment.Development;
  const authContext = useAuth();

  const {t} = useTranslation();

  const {url} = useRouteMatch();

  const {pathname} = useLocation();

  const {hasReadPermission} = useAuth();

  const personalDataPaths = useMemo(
    () => [
      `${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
      `${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Caregivers}`,
      `${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Health}`,
      `${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Attachments}`,
    ],
    []
  );

  const digitalInclusionPaths = useMemo(
    () => [
      `${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Applications}`,
      `${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Devices}`,
      `${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_UserCards}`,
      `${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Playlists}`,
    ],
    []
  );

  const canReadSeniorPersonalData = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Profile) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Caregivers) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Health) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Attachments),
    [hasReadPermission]
  );

  const canReadSeniorDigitalInclusion = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Applications) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Devices) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Playlists) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Cards),
    [hasReadPermission]
  );

  const availableActivitiesSectors = useMemo(() => {
    if (!activitiesSectors) return activitiesSectors;

    return activitiesSectors.filter(({id}: ActivitiesSector) => {
      const permissionType = SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[id!];

      return permissionType && hasReadPermission(permissionType);
    });
  }, [activitiesSectors, hasReadPermission]);

  const canReadSeniorCarePlan =
    !!availableActivitiesSectors?.length ||
    hasReadPermission(RolePermissionType.Seniors_CareAndActivities);

  const firstCarePlanPath = availableActivitiesSectors?.[0]?.id
    ? `${url}${ScreenPaths.Senior_CarePlan.replace(
        ':activitiesSectorId',
        availableActivitiesSectors[0].id
      )}`
    : `${url}${ScreenPaths.Senior_CareAndActivity}`;

  const handleExpandClick = () => setExpandedPersonalData(!expandedPersonalData);
  const handleExpandStatisticsClick = () => setExpandedStatistics(!expandedStatistics);
  const handleExpandCarePlanClick = () => setExpandedCarePlan(!expandedCarePlan);
  const handleExpandDigitalInclusionClick = () =>
    setExpandedDigitalInclusion(!expandedDigitalInclusion);

  useEffect(() => {
    const isPersonalDataRoute = personalDataPaths.some((path) => pathname.includes(path));
    setExpandedPersonalData(isPersonalDataRoute);

    const isStatisticsRoute = pathname.includes(ScreenPaths.Senior_Dashboard);
    setExpandedStatistics(isStatisticsRoute);

    const isCarePlanRoute =
      pathname.includes(ScreenPaths.Senior_CarePlan.split('/:activitiesSectorId')[0]) ||
      pathname.includes(ScreenPaths.Senior_CareAndActivity);
    setExpandedCarePlan(isCarePlanRoute);

    const isDigitalInclusionRoute = digitalInclusionPaths.some((path) => pathname.includes(path));
    setExpandedDigitalInclusion(isDigitalInclusionRoute);
  }, [digitalInclusionPaths, pathname, personalDataPaths]);

  // Main list styling
  const listItemsSx = {
    width: '180px',
    height: '40px',
    boxShadow: 'none',
    mx: '11px',
    px: '4px',
    '& .MuiTypography-root': {
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: '14px',
      fontWeight: 500,
    },
  };

  const skeletonSx = {
    borderRadius: '5px',
    mx: '12px',
    my: '8px',
  };

  // Dropdown list styling
  const dropdownListItemsSx = {
    ...listItemsSx,
    color: '#747474',
    backgroundColor: '#F9FBFF',
    paddingLeft: sidePanelExpanded ? '22px' : '0px',
    paddingRight: '0px',
    '& .MuiTypography-root': {
      fontSize: '12px',
      fontWeight: 500,
      color: '#747474',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '15px',
      color: '#747474',
    },
    '& .list-item-link-icon': {
      width: '18px',
      height: '18px',
    },
    '& .list-item-link-icon svg': {
      width: '18px',
      height: '18px',
    },
    '&:hover': {
      filter: 'brightness(95%)',
      backgroundColor: '#F9FBFF',
    },
  };

  return (
    <>
      {hasReadPermission(RolePermissionType.Seniors_Dashboard) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.Senior_Dashboard}`}
            primary={t(translations.screen_paths.senior_dashboard)}
            icon={<InsertChart className="list-item-link-icon" />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleExpandStatisticsClick();
                  e.preventDefault();
                }}
                sx={{rotate: expandedStatistics ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamFirstChild
            sx={listItemsSx}
            isActive={(_match, location) =>
              location.pathname.includes(ScreenPaths.Senior_Dashboard)
            }
          />
          <Collapse
            in={expandedStatistics}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            {hasReadPermission(RolePermissionType.Seniors_Dashboard_Activity) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_Dashboard}${ScreenPaths.Senior_Dashboard_Activity}`}
                primary={t(translations.screen_paths.senior_activity)}
                icon={<ViewComfyRounded />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_Dashboard_GeneralStatistics) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_Dashboard}${ScreenPaths.Senior_Dashboard_General}`}
                primary={t(translations.seniorSideMenu.generalStatistics)}
                icon={<BarChart />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_Dashboard_HealthStatistics) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_Dashboard}${ScreenPaths.Senior_Dashboard_Health}`}
                primary={t(translations.seniorSideMenu.healthStatistics)}
                icon={<Favorite />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}

      {canReadSeniorPersonalData && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.Senior_PersonalData}`}
            primary={t(translations.seniorSideMenu.personalData)}
            icon={<AccountCircle />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleExpandClick();
                  e.preventDefault();
                }}
                sx={{rotate: expandedPersonalData ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamMiddleChild
            sx={listItemsSx}
            isActive={(_match, location) =>
              personalDataPaths.some((path) => location.pathname.includes(path))
            }
          />

          <Collapse
            in={expandedPersonalData}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            {hasReadPermission(RolePermissionType.Seniors_PersonalData_Profile) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`}
                primary={t(translations.screen_paths.senior_profile)}
                icon={<PersonRounded />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_PersonalData_Caregivers) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Caregivers}`}
                primary={t(translations.screen_paths.senior_caregivers)}
                icon={<People />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_PersonalData_Health) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Health}`}
                primary={t(translations.screen_paths.senior_health)}
                icon={<Favorite />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_PersonalData_Attachments) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Attachments}`}
                primary={t(translations.screen_paths.senior_attachments)}
                icon={<DriveFolderUploadRounded />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}

      {activitiesSectors === undefined && (
        <>
          <Skeleton
            animation="wave"
            variant="rectangular"
            width={officeContext.state.sideNavigationDrawerCollapsed ? 40 : 178}
            height={40}
            sx={skeletonSx}
          />
          <Skeleton
            animation="wave"
            variant="rectangular"
            width={officeContext.state.sideNavigationDrawerCollapsed ? 40 : 178}
            height={40}
            sx={skeletonSx}
          />
        </>
      )}
      {activitiesSectors === null && (
        <Alert severity="error">
          {t(translations.settingsSideMenu.activities_sectors.load_error)}
        </Alert>
      )}
      {canReadSeniorCarePlan && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={firstCarePlanPath}
            primary={t(translations.screen_paths.senior_care_plan)}
            icon={<Widgets className="list-item-link-icon" />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleExpandCarePlanClick();
                  e.preventDefault();
                }}
                sx={{rotate: expandedCarePlan ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamMiddleChild
            sx={listItemsSx}
            isActive={(_match, location) =>
              location.pathname.includes(
                ScreenPaths.Senior_CarePlan.split('/:activitiesSectorId')[0]
              ) || location.pathname.includes(ScreenPaths.Senior_CareAndActivity)
            }
          />
          <Collapse
            in={expandedCarePlan}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            {availableActivitiesSectors?.map(({id, name, iconUrl}: ActivitiesSector) => (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                key={`activities-sector-link-${id}`}
                primary={name}
                to={`${url}${ScreenPaths.Senior_CarePlan.replace(':activitiesSectorId', id ?? '')}`}
                icon={<ReactSVG className="list-item-link-icon" src={iconUrl} title={name} />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            ))}
            {hasReadPermission(RolePermissionType.Seniors_CareAndActivities) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_CareAndActivity}`}
                primary={t(translations.seniorSideMenu.careAndActivity)}
                icon={<Assignment className="list-item-link-icon" />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}

      {hasReadPermission(RolePermissionType.Seniors_IndividualPlan) &&
        authContext.state.session?.selectedAffiliation?.entity.specialPurpose ===
          EntitySpecialPurpose.Testing && (
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.Seniors_IndividualPlan}`}
            primary={t(translations.seniorSideMenu.individualPlan)}
            icon={<SettingsAccessibilityIcon className="list-item-link-icon" />}
            teamMiddleChild
            sx={listItemsSx}
          />
        )}

      {canAccessDevelopmentFeatures &&
        hasReadPermission(RolePermissionType.Seniors_PersonalData_Reminders) && (
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.Senior_Reminders}`}
            primary={t(translations.reminders.title)}
            icon={<Alarm className="list-item-link-icon" />}
            teamMiddleChild
            sx={listItemsSx}
          />
        )}

      {hasReadPermission(RolePermissionType.Seniors_CareServices) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.Senior_CareServices}`}
          primary={t(translations.seniorSideMenu.careServices)}
          icon={<AccessTimeFilled className="list-item-link-icon" />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {canReadSeniorDigitalInclusion && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.Senior_DigitalInclusion}`}
            primary={t(translations.role_permissions_section['digital-inclusion'])}
            icon={<VideoSettings className="list-item-link-icon" />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleExpandDigitalInclusionClick();
                  e.preventDefault();
                }}
                sx={{rotate: expandedDigitalInclusion ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamMiddleChild
            sx={listItemsSx}
            isActive={(_match, location) =>
              digitalInclusionPaths.some((path) => location.pathname.includes(path))
            }
          />

          <Collapse
            in={expandedDigitalInclusion}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            {hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Applications) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Applications}`}
                primary={t(translations.screen_paths.senior_applications)}
                icon={<Apps />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Devices) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Devices}`}
                primary={t(translations.screen_paths.senior_devices)}
                icon={<Devices />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Cards) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_UserCards}`}
                primary={t(translations.screen_paths.senior_user_cards)}
                icon={<Badge />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Playlists) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Playlists}`}
                primary={t(translations.screen_paths.senior_playlists)}
                icon={<MusicNote />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}
    </>
  );
};
